/**
 * The per-SKU ADMISSION surface: how many asynchronous tasks one account may
 * hold in flight, per tier class (#1900, parent #1838 ADR 6 / review item R4;
 * revalidated standalone by #1883).
 *
 * ── WHAT THIS DOCUMENT IS, AND WHAT IT IS EMPHATICALLY NOT ────────────────
 *
 * It is a COUNTER's limit, not an amount. It never enters a bill, a report or
 * a reconciliation, and it never touches a balance. The ledger shape under it
 * is byte-identical to plain settle-on-completion: nothing at submit, one
 * event on `Success`, nothing on failure. ADR-013 records that; ADR-009 §3 is
 * satisfied for the same reason `pub_account_flags.can_spend` satisfies it —
 * a limit on a count of the gateway's OWN in-flight rows is not money and is
 * not derived from any price.
 *
 * That is also why it may cross to the gateway at all. #1838's meta-rule —
 * *any gate on the tenant path must be computable from gateway-local facts
 * alone* — leaves exactly three ways to feed one, and this takes the first:
 * data published through the pub snapshot, carrying no amount. The count it
 * is compared against is the gateway's own task table. Platform can be down
 * and this gate still answers.
 *
 * ── WHY A MAP AND NOT A SCALAR ───────────────────────────────────────────
 *
 * #1838 priced a single seedance submission from ¥1.85 (the 480P/4s anchor) up
 * to roughly **¥268** for the most expensive admitted request — about a 145x
 * spread inside one SKU family. A global `N` therefore bounds nothing anyone
 * can name: listing a higher tier silently multiplies the exposure that `N`
 * was chosen against, and nobody revisits `N` when listing a SKU. R4's ruling
 * is that the limit must be *attached to the tier data*, so that listing a
 * new tier cannot happen without someone writing down that tier's concurrency
 * allowance.
 *
 * ── STANDALONE SINCE #1883: THE TIER CLASSES ARE THIS MAP'S OWN KEYS ──────
 *
 * This document used to be validated as a PAIR with the parameter surface:
 * `tier_class_parameter` had to name one of the surface's enum rules, and the
 * allowance map's key set had to EQUAL that rule's admitted values, verbatim,
 * in both directions. #1883 retired the parameter surface — the ark
 * vocabulary is protocol code (`ark-task-parameters.ts`) and value domains
 * are the upstream's — so there is no enumerated value list left to equal.
 *
 * What replaces the equality is simpler and carries the same R4 force: **the
 * tier classes ARE the keys of this map.** A request whose tier-class value
 * matches no key is refused at the route gate before any spend, so a tier
 * nobody sized is a tier nobody can submit — R4's "listing a tier forces
 * someone to write down its exposure" now holds because writing the key IS
 * listing the tier. The quantity this bounds is unchanged:
 *
 *   worst-case unsettled exposure
 *     = (max in-flight tasks) x (the most expensive request admitted WITHIN
 *                                that tier class)
 *
 * and the second factor's ceiling is now the upstream's own domain rather
 * than a transcribed bound — which the operator must account for when they
 * choose the allowance, and which #1951's reconciliation probes watch.
 *
 * ── MATCHING IS CASE-INSENSITIVE, AND DECLARED SPELLINGS MUST NOT COLLIDE ─
 *
 * The old resolver folded a tenant's spelling onto the surface's declared
 * one, so a verbatim key lookup was safe. With domains upstream-owned the
 * canonical value is the tenant's own spelling, so the gate matches keys
 * case-insensitively ({@link arkTaskAllowanceForTierClass}) — `480p` finds a
 * `480P` key — and this validator refuses two keys that collide under the
 * fold, because one of them could never be selected deterministically.
 *
 * ── THE COUNTER IS PER ACCOUNT; ONLY THE LIMIT IS PER TIER CLASS ──────────
 *
 * Decided by the owner, 2026-08-13, and unchanged: admission asks "how many
 * tasks does this ACCOUNT hold?" and compares that one number against the
 * allowance bound to the INCOMING request's tier class. With `{480P: 3,
 * 4k: 1}`, an account holding three `480P` tasks is refused a `4k`
 * submission, because 3 >= 1.
 */
import {
  arkTaskParameterNamed,
  arkTaskParametersForVocabulary,
  isArkTaskParameterVocabularyId,
  type ArkTaskParameterVocabularyId,
} from './ark-task-parameters.ts';

/**
 * One SKU's admission rule.
 *
 * `tier_class_parameter` names a parameter of the ark protocol vocabulary,
 * and `max_in_flight_by_tier_class` gives one allowance per tier class it
 * serves — the key set is the whole enumeration of servable tiers.
 */
export interface ArkTaskAdmissionSurface {
  /** The vocabulary parameter whose value names the tier class. */
  readonly tier_class_parameter: string;
  /** Tier class -> maximum tasks one ACCOUNT may hold in flight. */
  readonly max_in_flight_by_tier_class: Readonly<Record<string, number>>;
  /**
   * Which protocol vocabulary this SKU sells.
   *
   * Omitted = `v1`: every SKU stored before this field existed, and every
   * SKU not yet cut over. That omission is the compatibility core — a
   * re-read of a pre-V2 document is byte-identical and the route gate
   * still resolves against {@link ARK_TASK_PARAMETERS_V1}.
   *
   * `"v2"` is an operator action, not a default. How to cut a SKU over:
   * PUT the protocol registration with this field set to `"v2"` on the
   * existing `task_admission` document (the other two keys stay). No SQL
   * migration: the column is JSON. Do not switch {@link ARK_TASK_PARAMETERS}
   * itself; that alias is V1 on purpose.
   *
   * Seed: V2 admits the name because 2.0 supports it. 2.5 returns
   * `unsupported_seed`. That is a SKU / upstream-domain difference, not a
   * reason to keep `seed` out of V2 or to refuse it here.
   */
  readonly parameter_vocabulary?: ArkTaskParameterVocabularyId;
}

export type ArkTaskAdmissionSurfaceViolation =
  | 'not_an_object'
  | 'invalid_tier_class_parameter'
  /** The named parameter is outside the protocol vocabulary. */
  | 'unknown_tier_class_parameter'
  | 'allowances_not_an_object'
  /** An empty map declares no servable tier at all — an unservable listing. */
  | 'allowances_empty'
  /** A key that is not a speakable tier-class value. */
  | 'invalid_tier_class'
  /** Two keys collide under the case fold the gate matches with. */
  | 'duplicate_tier_class'
  | 'invalid_allowance'
  /** `parameter_vocabulary` was present and is not a known version id. */
  | 'unknown_parameter_vocabulary';

export type ArkTaskAdmissionSurfaceValidation =
  | { readonly ok: true; readonly admission: ArkTaskAdmissionSurface }
  | { readonly ok: false; readonly violations: readonly ArkTaskAdmissionSurfaceViolation[] };

/**
 * The largest allowance a document may declare.
 *
 * A bound on the DOCUMENT, not a domain fact: it exists so a mistyped
 * operator value cannot turn a one-task exposure into an unbounded one in a
 * single keystroke. The shipping value for every tier is 1; anything
 * approaching this ceiling is a commercial decision that should arrive with
 * an argument, not with a typo.
 */
export const MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE = 64;

/**
 * A speakable tier-class key: the same alphabet a parameter VALUE may carry
 * (`ark-task-parameters.ts`'s garbage cap), because a key is matched against
 * canonical parameter values and a key nothing could ever spell is a dead
 * allowance its author believes is in force.
 */
const TIER_CLASS_KEY = /^[A-Za-z0-9:._-]{1,64}$/;

/**
 * Validate an admission document, alone.
 *
 * The returned document is REBUILT rather than echoed, preserving the
 * operator's key order, so what is stored has no unknown keys, has a stable
 * byte form, and round-trips through this function unchanged.
 *
 * ── PROTOTYPE KEYS, HANDLED RATHER THAN ASSUMED AWAY ─────────────────────
 *
 * `TIER_CLASS_KEY` admits `constructor` and every other `Object.prototype`
 * member, and this document arrives from `JSON.parse`, so its prototype
 * chain is live. Presence is therefore tested with `Object.hasOwn` and
 * enumeration with `Object.keys` — never a bare index — so an inherited
 * function can neither pass as an allowance nor make a missing key look
 * present.
 */
export function validateArkTaskAdmissionSurface(
  value: unknown,
): ArkTaskAdmissionSurfaceValidation {
  const record = asRecord(value);
  if (record === null) return { ok: false, violations: ['not_an_object'] };

  const parameterName = record['tier_class_parameter'];
  if (typeof parameterName !== 'string' || parameterName.length === 0) {
    return { ok: false, violations: ['invalid_tier_class_parameter'] };
  }
  const rawVocabulary = record['parameter_vocabulary'];
  if (rawVocabulary !== undefined && !isArkTaskParameterVocabularyId(rawVocabulary)) {
    return { ok: false, violations: ['unknown_parameter_vocabulary'] };
  }
  const vocabularyId: ArkTaskParameterVocabularyId = rawVocabulary ?? 'v1';
  const vocabulary = arkTaskParametersForVocabulary(vocabularyId);
  if (arkTaskParameterNamed(parameterName, vocabulary) === null) {
    // Outside the protocol vocabulary this SKU sells: no request could
    // ever carry a value for it, so every submission would refuse
    // `tier_class_unresolved` — a permanently-broken listing discovered
    // by a tenant rather than by the operator who made it. Looked up in
    // the selected version so a V2-only name is legal only on a V2 SKU.
    return { ok: false, violations: ['unknown_tier_class_parameter'] };
  }

  const rawAllowances = asRecord(record['max_in_flight_by_tier_class']);
  if (rawAllowances === null) {
    return { ok: false, violations: ['allowances_not_an_object'] };
  }
  const keys = Object.keys(rawAllowances);
  if (keys.length === 0) {
    return { ok: false, violations: ['allowances_empty'] };
  }

  const violations: ArkTaskAdmissionSurfaceViolation[] = [];
  const allowances: Record<string, number> = {};
  const folded = new Set<string>();
  for (const tierClass of keys) {
    if (!TIER_CLASS_KEY.test(tierClass)) {
      violations.push('invalid_tier_class');
      continue;
    }
    // Folded, because the gate matches case-insensitively (see the header):
    // two spellings that differ only in case would make one allowance
    // unreachable, silently.
    const fold = tierClass.toLowerCase();
    if (folded.has(fold)) {
      violations.push('duplicate_tier_class');
      continue;
    }
    folded.add(fold);
    const allowance = rawAllowances[tierClass];
    if (
      typeof allowance !== 'number' || !Number.isSafeInteger(allowance) ||
      allowance < 1 || allowance > MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE
    ) {
      // Zero is refused with the rest, not admitted as "this tier is off".
      // Removing the KEY is how a tier stops being served; two ways to say
      // one thing is the drift ADR-012 §3 spends its length warning about.
      violations.push('invalid_allowance');
      continue;
    }
    allowances[tierClass] = allowance;
  }

  if (violations.length > 0) return { ok: false, violations: [...new Set(violations)] };
  return {
    ok: true,
    admission: {
      tier_class_parameter: parameterName,
      max_in_flight_by_tier_class: allowances,
      // Appended, never inserted: a document that omitted the field stays
      // two keys in the same order, so a pre-V2 JSON round-trip is
      // byte-identical. Stated `'v1'` is kept rather than dropped, so an
      // operator who wrote the version down still sees it.
      ...(rawVocabulary === undefined ? {} : { parameter_vocabulary: rawVocabulary }),
    },
  };
}

/**
 * Read a stored admission document, or report its absence.
 *
 * The mirror of `resolveStoredMeteringSpec`, and it exists for the same
 * reason ADR-012 gives there: the column is TEXT, direct SQL can put anything
 * in it, and a reader that treated an unreadable document as "no gate" would
 * serve the SKU ungated. Both possible answers here mean REFUSE at the route
 * gate — there is no third answer and deliberately no default document.
 *
 * "Absent" and "illegal" are collapsed. Callers needing to tell an operator
 * which one they have must run {@link validateArkTaskAdmissionSurface}
 * themselves.
 */
export function resolveStoredArkTaskAdmission(
  raw: unknown,
): { readonly present: true; readonly admission: ArkTaskAdmissionSurface }
  | { readonly present: false } {
  if (raw === null || raw === undefined) return { present: false };
  if (typeof raw !== 'string') {
    const direct = validateArkTaskAdmissionSurface(raw);
    return direct.ok ? { present: true, admission: direct.admission } : { present: false };
  }
  if (raw.length === 0) return { present: false };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { present: false };
  }
  const validated = validateArkTaskAdmissionSurface(parsed);
  return validated.ok ? { present: true, admission: validated.admission } : { present: false };
}

/**
 * The allowance bound to one canonical tier-class value, matched
 * case-insensitively against the declared keys — or `null` for a value no
 * key covers, which the route gate refuses before any spend.
 *
 * Returns the DECLARED key spelling beside the allowance, so refusal
 * messages and the frozen `generation_tasks.tier_class` column speak the operator's
 * spelling whatever the tenant typed. Deterministic because the validator
 * refuses fold collisions.
 */
export function arkTaskAllowanceForTierClass(
  admission: ArkTaskAdmissionSurface,
  value: string,
): { readonly tierClass: string; readonly allowance: number } | null {
  const fold = value.toLowerCase();
  for (const tierClass of Object.keys(admission.max_in_flight_by_tier_class)) {
    if (tierClass.toLowerCase() !== fold) continue;
    const allowance = admission.max_in_flight_by_tier_class[tierClass];
    if (typeof allowance !== 'number' || !Number.isSafeInteger(allowance) || allowance < 1) {
      return null;
    }
    return { tierClass, allowance };
  }
  return null;
}

/**
 * Apply an org overlay to one request's tier class.
 *
 * The SKU document still owns which tiers exist: an overlay cannot invent a
 * key the SKU did not list. A missing overlay key falls back to the SKU
 * allowance. `null` overlay is the default — every org without a row.
 */
export function overlayArkTaskAllowance(
  base: ArkTaskAdmissionSurface,
  overlay: Readonly<Record<string, number>> | null,
  tierClassValue: string,
): { readonly tierClass: string; readonly allowance: number } | null {
  const fromBase = arkTaskAllowanceForTierClass(base, tierClassValue);
  if (fromBase === null) return null;
  if (overlay === null) return fromBase;
  const fromOverlay = arkTaskAllowanceForTierClass(
    {
      ...base,
      max_in_flight_by_tier_class: overlay,
    },
    tierClassValue,
  );
  return fromOverlay ?? fromBase;
}

/**
 * Parse an overlay map against a SKU document.
 *
 * Keys must fold-match a key the SKU already serves. Values use the same
 * 1…{@link MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE} bound as the SKU document.
 * Empty maps are refused: clearing an overlay is DELETE, not PUT {}.
 */
export function parseOrgSkuTaskAdmissionOverlay(
  value: unknown,
  base: ArkTaskAdmissionSurface,
):
  | { readonly ok: true; readonly overlay: Readonly<Record<string, number>> }
  | { readonly ok: false; readonly reason: string } {
  const record = asRecord(value);
  if (record === null) return { ok: false, reason: 'overlay must be an object' };
  const keys = Object.keys(record);
  if (keys.length === 0) return { ok: false, reason: 'overlay must name at least one tier class' };
  const overlay: Record<string, number> = {};
  const folded = new Set<string>();
  for (const tierClass of keys) {
    if (!TIER_CLASS_KEY.test(tierClass)) {
      return { ok: false, reason: `invalid tier class ${tierClass}` };
    }
    const fold = tierClass.toLowerCase();
    if (folded.has(fold)) {
      return { ok: false, reason: `duplicate tier class ${tierClass}` };
    }
    folded.add(fold);
    if (arkTaskAllowanceForTierClass(base, tierClass) === null) {
      return { ok: false, reason: `tier class ${tierClass} is not on this SKU` };
    }
    const allowance = record[tierClass];
    if (
      typeof allowance !== 'number' || !Number.isSafeInteger(allowance) ||
      allowance < 1 || allowance > MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE
    ) {
      return { ok: false, reason: `invalid allowance for ${tierClass}` };
    }
    overlay[tierClass] = allowance;
  }
  return { ok: true, overlay };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}
